import { StickerSquare } from "untitledui-js-base";
import { ActivityNote } from "shared/app/Notes/ActivityNote";
import { useAuthContext } from "providers/Auth";
import { useMemo, useRef } from "react";
import { instance } from "api/apiBase";
import {
  getMentionNotificationId,
  isUnread,
  useMarkRead
} from "shared/app/Notes/helpers";
import { useQueryClient } from "@tanstack/react-query";
import { NoteForm } from "shared/app/Notes/components/NoteForm";
import { ApplicationStatus } from "shared/types";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Button, ButtonSize, Show } from "shared/components";
import { ViewIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useNavigate } from "react-router-dom";
import { NotesTabs } from "./components/NotesTabs";
import { useNotesController } from "shared/queries/useNotesController";
import { AutoLoadMoreOnScroll } from "shared/app/Notes/components/AutoLoadMoreOnScroll";

export const NotesList = ({
  applicationId,
  notesController,
  propertyName,
  applicationStatus,
  applicantsNames,
  showViewApplicationButton,
  onMarkReadSuccess
}: {
  applicationId: string;
  propertyName?: string;
  applicationStatus?: ApplicationStatus;
  applicantsNames?: string;
  notesController: ReturnType<typeof useNotesController>;
  showViewApplicationButton?: boolean;
  onMarkReadSuccess?: () => void;
}) => {
  const { auth } = useAuthContext();
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    addNoteMeta,
    editNoteMeta,
    deleteNoteMeta,
    showingNotesType,
    showingNotes
  } = notesController;

  const scrollRootRef = useRef<HTMLDivElement | null>(null);

  const ureadNotes = useMemo(() => {
    const newUnreadNotes: { id: string; read: boolean }[] = [];
    const userId = auth?.id;
    if (userId == null) return newUnreadNotes;

    for (const note of showingNotes)
      if (isUnread(note, userId))
        newUnreadNotes.push({
          id: String(getMentionNotificationId(note, userId) ?? ""),
          read: false
        });

    return newUnreadNotes;
  }, [showingNotes, auth?.id]);

  const getElById = (id: string) =>
    document.querySelector<HTMLElement>(`[data-activity-id="${id}"]`);

  async function markReadBatch(ids: string[]) {
    await instance
      .post("/portal/v1/notifications/mark_as_read", {
        ids: ids.map(Number)
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
        });
        onMarkReadSuccess?.();
      });
  }

  useMarkRead(ureadNotes, getElById, markReadBatch, {
    threshold: 0.5,
    dwellMs: 600,
    flushMs: 300
  });

  const handleViewApplication = () => {
    navigate(`/applications/${applicationId}`);
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 py-4 border-b space-y-4">
        <div className="space-y-2">
          <ApplicationStatusBadge
            type="application"
            status={applicationStatus}
            className="text-xs font-semibold"
          />
          <p className="text-sm font-bold text-content-1000">
            {applicantsNames}
          </p>
        </div>
        <Show if={showViewApplicationButton}>
          <Button
            HugeIcon={ViewIcon}
            size={ButtonSize.XS}
            onClick={handleViewApplication}
          >
            View application
          </Button>
        </Show>
      </div>
      <div className="p-6 border-b">
        <NoteForm
          onSubmit={async (data) => {
            await addNoteMeta.mutateAsync(data);
          }}
          applicationId={String(applicationId)}
          propertyName={propertyName}
          isLoading={addNoteMeta.isPending}
          editorContainerClassName="min-h-[100px]"
        />
      </div>

      <div className="flex flex-col flex-grow bg-background-900 space-y-6 px-6 overflow-hidden">
        <NotesTabs
          notesController={notesController}
          tabClassName="mt-4 !ml-0 mr-6"
        />
        {!showingNotes.length ? (
          <div className="mt-8 flex flex-auto flex-col items-center justify-center">
            <div className="mb-6 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-alpha-40 p-5">
              <StickerSquare size="24" className="stroke-content-500" />
            </div>
            <p className="text-sm font-medium text-content-300">No logs yet</p>
          </div>
        ) : (
          <div
            ref={scrollRootRef}
            className="size-full flex-grow overflow-x-hidden overflow-y-auto bg-background-900"
          >
            <div className="border-alpha-100 pt-1 w-full space-y-6 pb-6">
              {showingNotes.map((note) => {
                return (
                  <ActivityNote
                    key={showingNotesType + note.updated_at + note.created_at}
                    propertyName={propertyName}
                    applicationId={String(applicationId)}
                    note={note}
                    deleteNoteMeta={deleteNoteMeta}
                    editNoteMeta={editNoteMeta}
                  />
                );
              })}
              <AutoLoadMoreOnScroll
                rootRef={scrollRootRef}
                hasNextPage={Boolean(notesController.notesMeta.hasNextPage)}
                isFetchingNextPage={
                  notesController.notesMeta.isFetchingNextPage
                }
                onLoadMore={() => notesController.notesMeta.fetchNextPage()}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
